import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  Icon,
  TextField,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import Africastalking from "africastalking-client";
import MDButton from "theme/components/MDButton";

// Dial pad layout key mapping
const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0"];

export default function Dialer({
  dialerToken,
  onIncomingCall,
  onClose, // Added onClose prop callback to drive the top-right close button action
}) {
  const [state, setState] = useState("notready");
  const [isReady, setIsReady] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timer, setTimer] = useState(0);
  const [wakeLock, setWakeLock] = useState(null);
  const [micReady, setMicReady] = useState(false);

  const ringtoneRef = useRef(null);
  const clientRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(0);
  const directionRef = useRef(null); // "inbound" | "outbound"

  let reconnectTimeout;

  // Global Exception Handlers
  window.addEventListener("unhandledrejection", (event) => {
    console.error("UNHANDLED PROMISE:", event.reason);
  });

  window.addEventListener("error", (event) => {
    console.error("GLOBAL ERROR:", event.error);
  });

  // Automatically check permission status when component mounts
  useEffect(() => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "microphone" })
        .then((permissionStatus) => {
          if (permissionStatus.state === "granted") {
            init(); // If already allowed by browser, bypass guard immediately
          }

          // Watch for changes while the application is running
          permissionStatus.onchange = () => {
            if (permissionStatus.state === "granted") {
              init();
            } else {
              setMicReady(false);
            }
          };
        })
        .catch(() => {
          // Fallback if browser permission query fails or is unsupported
          checkMediaDevicesDirectly();
        });
    } else {
      checkMediaDevicesDirectly();
    }
  }, [dialerToken]);

  // Fallback direct check for browsers that do not support permissions query api
  const checkMediaDevicesDirectly = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasMic = devices.some((device) => device.kind === "audioinput" && device.label);
      if (hasMic) {
        init();
      }
    } catch (err) {
      console.warn("Media device enumeration failed: ", err);
    }
  };

  async function init() {
    try {
      // Re-use current media track if it's already active and running
      if (!streamRef.current || !streamRef.current.active) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
      }
      setMicReady(true);
      setupClient(dialerToken);
    } catch (err) {
      console.error(err);
      setState("micdenied");
    }
  }

  function setupClient(validToken) {
    if (!validToken) return;

    if (clientRef.current) {
      try {
        clientRef.current.destroy?.();
      } catch {}
    }

    const client = new Africastalking.Client(validToken);
    clientRef.current = client;

    clientRef.current.on("ready", () => {
      setState("ready");
      setIsReady(true);
    });

    clientRef.current.on("notready", () => {
      setState("notready");
      setIsReady(false);
    });

    clientRef.current.on("calling", () => setState("calling"));

    clientRef.current.on("incomingcall", () => {
      directionRef.current = "inbound";
      setState("incomingcall");
      ringtoneRef.current?.play().catch(() => {});
      onIncomingCall?.();
    });

    clientRef.current.on("callaccepted", () => {
      setState("callaccepted");
      ringtoneRef.current?.pause();
      if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;
    });

    clientRef.current.on("hangup", () => {
      setState("hangup");
      ringtoneRef.current?.pause();
      if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;
      setTimeout(() => setState("ready"), 1500);
    });

    clientRef.current.on("offline", reconnect);
    clientRef.current.on("closed", reconnect);
  }

  function reconnect() {
    setState("offline");
    setIsReady(false);
    clearTimeout(reconnectTimeout);
    reconnectTimeout = setTimeout(() => {
      init();
    }, 2000);
  }

  useEffect(() => {
    ringtoneRef.current = new Audio("/audio/002.mp3");
    ringtoneRef.current.loop = true;

    return () => {
      // Clean up sound track on unmount
      ringtoneRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  // =========================
  // WAKE LOCK & TIMER MANAGERS
  // =========================
  const requestWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        const lock = await navigator.wakeLock.request("screen");
        setWakeLock(lock);
      }
    } catch {}
  };

  const releaseWakeLock = async () => {
    if (wakeLock) {
      await wakeLock.release();
      setWakeLock(null);
    }
  };

  useEffect(() => {
    if (state === "calling" || state === "callaccepted") {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [state]);

  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        (state === "calling" || state === "callaccepted")
      ) {
        requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [state]);

  useEffect(() => {
    let interval;
    if (state === "callaccepted") {
      interval = setInterval(() => setTimer((t) => t + 1), 1000);
    } else {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [state]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // =========================
  // DIAL PAD MUTATORS
  // =========================
  function handleKeyPress(key) {
    setPhoneNumber((prev) => (prev || "") + key);
  }

  function handleDelete() {
    setPhoneNumber((prev) => (prev ? prev.slice(0, -1) : ""));
  }

  // =========================
  // EXTERNAL TELEPHONY ENGINE CALLS
  // =========================
  const handleCall = async () => {
    if (!isReady || !phoneNumber || !clientRef.current) return;

    directionRef.current = "outbound";
    setTimeout(() => {
      clientRef.current.call(phoneNumber);
    }, 300);
  };

  const handleHangup = () => {
    if (!isReady) return;
    clientRef.current.hangup();
  };

  const handleAccept = () => {
    if (!isReady) return;
    clientRef.current.answer();
  };

  const handleReject = () => {
    if (!isReady) return;
    clientRef.current.hangup();
  };

  const handleWindowReload = () => {
    window.location.reload();
  };

  const config = {
    ready: { label: "Ready", color: "success", icon: "call" },
    notready: { label: "Not Ready", color: "default", icon: "phone_disabled" },
    calling: { label: "Calling...", color: "success", icon: "call" },
    incomingcall: { label: "Incoming Call", color: "info", icon: "call_received" },
    callaccepted: { label: "In Call", color: "success", icon: "call" },
    hangup: { label: "Call Ended", color: "error", icon: "call_end" },
    offline: { label: "Offline", color: "error", icon: "wifi_off" },
    closed: { label: "Disconnected", color: "default", icon: "wifi_off" },
  }[state];

  // Calculate if an active running call session context exists
  const isCallSessionActive = ["calling", "incomingcall", "callaccepted", "hangup"].includes(state);

  return (
    <Box sx={{ width: "100%", maxWidth: 420, mx: "auto", position: "relative" }}>
      {/* Action header bar handling top-left window refresh and top-right drawer close layout */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 1,
          px: 1,
        }}
      >
        <IconButton onClick={handleWindowReload} aria-label="reload window">
          <Icon>refresh</Icon>
        </IconButton>
        <IconButton onClick={onClose} aria-label="close dialer">
          <Icon>close</Icon>
        </IconButton>
      </Box>

      {/* Guard view: System Mic validation layer */}
      {!micReady ? (
        <Box p={3}>
          <Stack spacing={4}>
            <Box align="center">
              <Avatar sx={{ width: 72, height: 72, bgcolor: "warning.main" }}>
                <Icon sx={{ fontSize: 36 }}>mic</Icon>
              </Avatar>
            </Box>
            <Box textAlign="center" p={0}>
              <Typography variant="h6" fontWeight={700}>
                Microphone Access
              </Typography>
            </Box>
            <MDButton color="dark" variant="contained" startIcon={<Icon>mic</Icon>} onClick={init}>
              Enable Microphone
            </MDButton>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0, textAlign: "center" }}>
          {/* Connection status header */}
          <Box mt={0}>
            <Chip label={`Status: ${config?.label || state}`} color={config?.color || "default"} />
          </Box>

          <Box mt={2} mb={3}>
            <Divider />
          </Box>

          {/* ========================================================= */}
          {/* VIEW 1: DIAL PAD GRID (Visible only when idle/ready/offline) */}
          {/* ========================================================= */}
          {!isCallSessionActive && (
            <Box>
              <Box>
                <TextField
                  fullWidth
                  value={phoneNumber || ""}
                  placeholder="Enter phone number"
                  onChange={(e) => {
                    // Allows keyboard typing updates to flow into the state engine properly
                    setPhoneNumber(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    // 1. Allow structural system shortcuts (Ctrl/Cmd + V, C, A, X)
                    const isSystemShortcut =
                      (e.ctrlKey || e.metaKey) &&
                      ["v", "c", "a", "x"].includes(e.key.toLowerCase());

                    // 2. Allow navigation, clearing, and structural tracking keys
                    const isUtilityKey = [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                      "Home",
                      "End",
                      "Enter",
                    ].includes(e.key);

                    // 3. Match pure digits (0-9) and the "+" sign explicitly
                    const isValidInputChar = /^[0-9+]$/.test(e.key);

                    // If it's none of the above, halt the event immediately
                    if (!isSystemShortcut && !isUtilityKey && !isValidInputChar) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData("text").replace(/[^0-9*#+]/g, "");
                    setPhoneNumber(pasted);
                  }}
                  inputProps={{
                    style: { textAlign: "center", fontSize: "28px", letterSpacing: "2px" },
                  }}
                />
              </Box>

              <Box mt={4}>
                <Grid container spacing={2}>
                  {keys.map((key) => (
                    <Grid item xs={4} key={key}>
                      <IconButton
                        onClick={() => handleKeyPress(key)}
                        sx={{
                          width: "100%",
                          height: 72,
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: "10%",
                          fontSize: 28,
                          fontWeight: "bold",
                        }}
                      >
                        {key}
                      </IconButton>
                    </Grid>
                  ))}
                  <Grid item xs={4}>
                    <IconButton
                      onClick={handleDelete}
                      sx={{
                        width: "100%",
                        height: 72,
                        border: "1px solid",
                        borderColor: "grey.300",
                        borderRadius: "10%",
                        fontSize: 28,
                        fontWeight: "bold",
                      }}
                    >
                      <Icon>backspace</Icon>
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>

              {/* Dialpad Submission Trigger action row */}
              <Box mt={5} display="flex" justifyContent="center">
                <IconButton
                  onClick={handleCall}
                  disabled={!isReady || !phoneNumber}
                  sx={{
                    width: 75,
                    height: 75,
                    backgroundColor: "success.main",
                    color: "#fff",
                    "&:hover": { backgroundColor: "success.dark" },
                    "&.Mui-disabled": {
                      backgroundColor: "action.disabledBackground",
                      color: "action.disabled",
                    },
                  }}
                >
                  <Icon>call</Icon>
                </IconButton>
              </Box>
            </Box>
          )}

          {/* ========================================================= */}
          {/* VIEW 2: ACTIVE CALL TRANSACTION WINDOW                     */}
          {/* ========================================================= */}
          {isCallSessionActive && (
            <Stack spacing={2} alignItems="center" width="100%">
              {state === "callaccepted" && (
                <Typography color="success.main" variant="h5" fontWeight="bold">
                  {formatTime(timer)}
                </Typography>
              )}

              <Typography variant="h4" sx={{ my: 1, fontWeight: "medium", letterSpacing: "1px" }}>
                {phoneNumber}
              </Typography>

              {/* ANIMATED TRANSITION BLOCKS */}
              {state === "calling" && (
                <Stack spacing={1} alignItems="center">
                  <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                    <Icon>person</Icon>
                  </Avatar>
                  <Icon sx={{ animation: "moveUp 1s infinite" }}>arrow_upward</Icon>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                    <Icon>call</Icon>
                  </Avatar>
                </Stack>
              )}

              {state === "incomingcall" && (
                <Stack spacing={1} alignItems="center">
                  <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                    <Icon>call</Icon>
                  </Avatar>
                  <Icon sx={{ animation: "moveDown 1s infinite" }}>arrow_downward</Icon>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                    <Icon>support_agent</Icon>
                  </Avatar>
                </Stack>
              )}

              {state === "callaccepted" && (
                <Box sx={{ position: "relative", width: 80, height: 80, my: 2 }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: "success.main",
                      transform: "translate(-50%, -50%)",
                      animation: "ripple 2s infinite",
                      opacity: 0.2,
                    }}
                  />
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: "success.main",
                      color: "#fff",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <Icon>call</Icon>
                  </Avatar>
                </Box>
              )}

              {state === "hangup" && (
                <Avatar sx={{ width: 64, height: 64, bgcolor: "error.main", color: "#fff" }}>
                  <Icon>call_end</Icon>
                </Avatar>
              )}

              {/* SYSTEM ACTION BUTTON CONTROL BOARD */}
              <Stack mt={3} spacing={1} width="100%">
                <Box mt={3}>
                  {state === "calling" && (
                    <MDButton
                      variant="contained"
                      fullWidth
                      color="error"
                      size="large"
                      onClick={handleHangup}
                    >
                      Cancel
                    </MDButton>
                  )}

                  {state === "incomingcall" && (
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <MDButton
                          variant="contained"
                          fullWidth
                          color="primary"
                          size="large"
                          onClick={handleAccept}
                        >
                          Accept
                        </MDButton>
                      </Grid>
                      <Grid item xs={6}>
                        <MDButton
                          variant="outlined"
                          fullWidth
                          color="error"
                          size="large"
                          onClick={handleReject}
                        >
                          Reject
                        </MDButton>
                      </Grid>
                    </Grid>
                  )}

                  {state === "callaccepted" && (
                    <MDButton
                      variant="contained"
                      fullWidth
                      color="error"
                      size="large"
                      onClick={handleHangup}
                    >
                      Hang Up
                    </MDButton>
                  )}
                </Box>
              </Stack>
            </Stack>
          )}
        </Box>
      )}

      {/* Animation Stylesheets */}
      <style>
        {`
          @keyframes moveUp {
            0% { transform: translateY(10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(-10px); opacity: 0; }
          }
          @keyframes moveDown {
            0% { transform: translateY(-10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(10px); opacity: 0; }
          }
          @keyframes ripple {
            0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.4; }
            70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
}
